import { useCallback, useEffect, useState } from "react";
import Spinner from "../../components/ui/Spinner";
import { ModalShell, btnGhost, btnPrimary, cardCls, errMsg, inputCls, labelCls } from "../../components/admin/promotionsUi";
import { useAdminToast } from "../../context/AdminToastContext";
import { formatISTDateTime } from "../../utils/dateUtils";
import { gamificationService } from "../../features/gamification/service";
import type { UserSearchResult, WalletLot, WalletLotsView } from "../../features/gamification/types";

import { LoadError, Pager, StatusBadge, fmtPaise, tdCls, thCls, usePaged, useCreditRate } from "./gamificationShared";
import { AuditTable } from "./GamificationAuditPanel";

// ── Take-backs ───────────────────────────────────────────────────────────────
const TAKEBACK_RESULT: Record<string, string> = {
  taken_back: "The reward was taken back.",
  already_taken_back: "This reward was already taken back. Nothing changed.",
  below_floor: "The balance would go below the floor, so nothing was taken. The credit was flagged for review.",
  window_closed: "The take-back window has closed for this reward.",
  not_takeable: "Only bonus credits can be taken back, not paid or old balances.",
  no_lot: "No credit record exists for this reward.",
  busy: "Another change is running on this wallet. Try again in a moment.",
  approval_required: "This take-back is large, so it was sent for approval. A different admin has to approve it in the Approvals tab.",
};

export function LotsTable({ lots, rate, onTakeback }: { lots: WalletLot[]; rate: number | null; onTakeback?: (l: WalletLot) => void }) {
  if (lots.length === 0) return <p className="text-gray-500 text-sm">This user has no credit records.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="text-xs text-gray-500">
            <th className={thCls}>Source</th>
            <th className={thCls}>Credited</th>
            <th className={thCls}>Left</th>
            <th className={thCls}>Status</th>
            <th className={thCls}>Granted</th>
            <th className={thCls}>Expires</th>
            {onTakeback && <th className="pb-2 font-medium" />}
          </tr>
        </thead>
        <tbody>
          {lots.map((l) => (
            <tr key={l.grant_key} className="border-t border-gray-800 text-gray-300">
              <td className={tdCls}>{(l.source ?? l.kind ?? "—").replace(/_/g, " ")}{l.kind === "free" ? "" : l.kind ? <span className="text-gray-500 text-xs"> ({l.kind.replace(/_/g, " ")})</span> : null}</td>
              <td className={tdCls}>{fmtPaise(l.credits_paise, rate)}</td>
              <td className={tdCls}>{fmtPaise(l.remaining_paise, rate)}</td>
              <td className={tdCls}><StatusBadge status={l.status} />{l.review_flag && <span className="text-amber-400 text-[11px] ml-1">needs review</span>}</td>
              <td className={`${tdCls} whitespace-nowrap`}>{formatISTDateTime(l.granted_at)}</td>
              <td className={`${tdCls} whitespace-nowrap`}>{l.expires_at ? formatISTDateTime(l.expires_at) : "Never"}</td>
              {onTakeback && (
                <td className="py-2">
                  {["free", "welcome", "topup_bonus"].includes(l.kind ?? "") && ["active", "expiring", "expired"].includes(l.status ?? "") && (
                    <button className={btnGhost} onClick={() => onTakeback(l)}>Take back</button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function TakebacksPanel() {
  const toast = useAdminToast();
  const exportCsv = async (run: () => Promise<void>, what: string) => {
    try {
      await run();
    } catch (e) {
      toast.showError(errMsg(e, `Failed to export the ${what}`));
    }
  };
  const rate = useCreditRate();
  const fetcher = useCallback((page: number) => gamificationService.listTakebacks(page), []);
  const { data, loading, error, load } = usePaged(fetcher, "Failed to load take-backs");
  useEffect(() => { void load(1); }, [load]);

  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<UserSearchResult[] | null>(null);
  const [searchError, setSearchError] = useState("");
  const [userId, setUserId] = useState("");
  const [lots, setLots] = useState<WalletLotsView | null>(null);
  const [lotsBusy, setLotsBusy] = useState(false);
  const [lotsError, setLotsError] = useState("");
  const [target, setTarget] = useState<WalletLot | null>(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState("");

  const search = async () => {
    const q = query.trim();
    if (q.length < 3) return setSearchError("Type at least 3 characters of a name, email or phone number.");
    setSearching(true);
    setSearchError("");
    try {
      setResults(await gamificationService.searchUsers(q, 10));
    } catch (e) {
      setResults(null);
      setSearchError(errMsg(e, "Couldn't search for users right now."));
    } finally {
      setSearching(false);
    }
  };

  const loadLots = async (id: string) => {
    const uid = id.trim();
    if (!uid) return;
    setUserId(uid);
    setLotsBusy(true);
    setLotsError("");
    setLots(null);
    setOutcome("");
    try {
      setLots(await gamificationService.getWalletLots(uid));
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      setLotsError(status === 404 ? "No user found with that id." : errMsg(e, "Couldn't load this user's credits."));
    } finally {
      setLotsBusy(false);
    }
  };

  const submit = async () => {
    if (!target || !reason.trim()) return;
    setSubmitting(true);
    try {
      const res = await gamificationService.takebackReward(userId, target.grant_key, reason.trim());
      const text = TAKEBACK_RESULT[res.status] ?? `Result: ${res.status}`;
      setOutcome(text);
      (res.status === "taken_back" || res.status === "approval_required" ? toast.showSuccess : toast.showError)(text);
      setTarget(null);
      setReason("");
      await loadLots(userId);
      setOutcome(text);
      void load(1);
    } catch (e) {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.showError(detail === "disabled" ? "Take-backs are switched off (rewards or wallet expiry is off in Settings)." : errMsg(e, "Failed to take back the reward"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className={cardCls}>
        <h2 className="text-white font-semibold mb-1">Take back a reward</h2>
        <p className="text-gray-500 text-xs mb-4">
          Find the user, pick the reward, give a reason. The reason is saved in the audit log. A reward can only be taken back inside the take-back window, and the balance can go below zero only down to the floor set in Settings.
          <span className="block text-gray-600">Example: a user got a reward for a fake deal, so take that reward back with the reason "fake deal".</span>
        </p>
        <form className="flex flex-wrap gap-2 items-end mb-3" onSubmit={(e) => { e.preventDefault(); void search(); }}>
          <div>
            <label className={labelCls} htmlFor="gt-search">Search by name, email or phone</label>
            <input id="gt-search" className={`${inputCls} max-w-sm`} value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <button type="submit" className={btnPrimary} disabled={searching || query.trim().length < 3}>{searching ? "Searching…" : "Search"}</button>
        </form>
        {searchError && <p className="text-red-400 text-sm mb-2" role="alert">{searchError}</p>}
        {results && results.length === 0 && <p className="text-gray-500 text-sm mb-2">No users match "{query.trim()}".</p>}
        {results && results.length > 0 && (
          <ul className="mb-3 divide-y divide-gray-800 border border-gray-800 rounded-lg">
            {results.map((u) => (
              <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm text-gray-300">
                <span>{u.name || "—"} · {u.email || u.phone || u.id} · <span className="capitalize">{u.role || "—"}</span></span>
                <button className={btnGhost} onClick={() => void loadLots(u.id)} disabled={lotsBusy}>Show credits</button>
              </li>
            ))}
          </ul>
        )}
        <form className="flex flex-wrap items-center gap-2 mb-4" onSubmit={(e) => { e.preventDefault(); void loadLots(userId); }}>
          <label className="text-xs text-gray-500" htmlFor="gt-id">or paste a user id</label>
          <input id="gt-id" className={`${inputCls} max-w-xs`} value={userId} onChange={(e) => setUserId(e.target.value)} />
          <button type="submit" className={btnGhost} disabled={lotsBusy || !userId.trim()}>{lotsBusy ? "Loading…" : "Show credits"}</button>
        </form>
        {lotsError && <p className="text-red-400 text-sm" role="alert">{lotsError}</p>}
        {outcome && <p className="text-sm text-indigo-300 bg-indigo-950/40 border border-indigo-900 rounded-lg px-3 py-2 mb-3" role="status">{outcome}</p>}
        {lots && (
          <div className="space-y-2">
            <p className="text-gray-400 text-xs">Balance {fmtPaise(lots.balance_paise, rate)}{lots.pending_spend_paise > 0 ? ` · ${fmtPaise(lots.pending_spend_paise, rate)} spend not yet applied to lots` : ""}</p>
            <LotsTable lots={lots.lots} rate={rate} onTakeback={(l) => { setTarget(l); setReason(""); }} />
          </div>
        )}
      </section>

      <section className={cardCls}>
        <h2 className="text-white font-semibold mb-1">Take-back history</h2>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <p className="text-gray-500 text-xs">Every take-back attempt and its result, newest first.</p>
          <button className={btnGhost} onClick={() => void exportCsv(() => gamificationService.exportTakebacks(), "take-backs")}>Export CSV</button>
        </div>
        {loading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : !data ? (
          <LoadError message={error} onRetry={() => void load(1)} />
        ) : data.items.length === 0 ? (
          <p className="text-gray-500 text-sm py-2">No take-backs yet.</p>
        ) : (
          <>
            <AuditTable rows={data.items} showUser />
            <Pager data={data} busy={loading} onPage={(p) => void load(p)} />
          </>
        )}
      </section>

      {target && (
        <ModalShell title="Take back this reward?" onClose={() => !submitting && setTarget(null)}>
          <p className="text-sm text-gray-300 mb-1">
            Amount: <strong>{fmtPaise(target.credits_paise, rate)}</strong> ({(target.source ?? "reward").replace(/_/g, " ")}, granted {formatISTDateTime(target.granted_at)}).
          </p>
          <p className="text-gray-500 text-xs mb-3">What the user has already spent is not returned: the balance may go below zero, down to the floor in Settings. The user is not charged below the floor.</p>
          <label className={labelCls} htmlFor="gt-reason">Reason (required, saved in the audit log)</label>
          <textarea id="gt-reason" className={inputCls} rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setTarget(null)} disabled={submitting} className={btnGhost}>Cancel</button>
            <button onClick={submit} disabled={submitting || !reason.trim()} className={btnPrimary}>{submitting ? "Taking back…" : "Take back"}</button>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

